// CreateMemberSexualRole.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberSexualRole.module.scss";
import { SEXUAL_ROLES } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q7) */
import mrImg from "@/assets/UI/memberCreate/q7Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q7Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q7MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q7MrsM.webp";

/* After-form cards images (q7) */
import dominantImg from "@/assets/UI/memberCreate/q7dominant.webp";
import submissiveImg from "@/assets/UI/memberCreate/q7submissive.webp";
import switchImg from "@/assets/UI/memberCreate/q7switch.webp";
import bratImg from "@/assets/UI/memberCreate/q7brat.webp";
import bratTamerImg from "@/assets/UI/memberCreate/q7bratTamer.webp";
import sensualRomanticImg from "@/assets/UI/memberCreate/q7sensualRomantic.webp";
import experimentalKinkyImg from "@/assets/UI/memberCreate/q7experimentalKinky.webp";
import voyeurImg from "@/assets/UI/memberCreate/q7voyeur.webp";
import exhibitionistImg from "@/assets/UI/memberCreate/q7exhibitionist.webp";
import serviceOrientedImg from "@/assets/UI/memberCreate/q7serviceOriented.webp";
import rolePlayerImg from "@/assets/UI/memberCreate/q7rolePlayer.webp";
import vanillaGentleImg from "@/assets/UI/memberCreate/q7vanillaGentle.webp";
import selfImg from "@/assets/UI/memberCreate/q7self.webp";

/* ---- Framer animations (same feel as CreateMemberGender) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook (same onboarding pattern) */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta per role */
const ROLE_META = {
  dominant: "😈",
  submissive: "😌",
  switch: "🔄",
  brat: "😏",
  brat_tamer: "😎",
  sensual_romantic: "💞",
  experimental_kinky: " 🔥",
  voyeur: "👀",
  exhibitionist: "🎭",
  service_oriented: "🤲",
  role_player: "🎲",
  vanilla_gentle: "🌙",
  self_describe: "📝",
  not_select: "🫧",
};

/* After-form cards */
const CARD_KEYS = [
  "dominant",
  "submissive",
  "switch",
  "brat",
  "brat_tamer",
  "sensual_romantic",
  "experimental_kinky",
  "voyeur",
  "exhibitionist",
  "service_oriented",
  "role_player",
  "vanilla_gentle",
  "self_describe",
];

const CARD_IMAGES = {
  dominant: dominantImg,
  submissive: submissiveImg,
  switch: switchImg,
  brat: bratImg,
  brat_tamer: bratTamerImg,
  sensual_romantic: sensualRomanticImg,
  experimental_kinky: experimentalKinkyImg,
  voyeur: voyeurImg,
  exhibitionist: exhibitionistImg,
  service_oriented: serviceOrientedImg,
  role_player: rolePlayerImg,
  vanilla_gentle: vanillaGentleImg,
  self_describe: selfImg,
};

function getClosingKey({ isCouple, rawGender }) {
  if (isCouple) return "onboarding.sexual_role.closing.couple";

  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.sexual_role.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.sexual_role.closing.woman";
  }
  return "onboarding.sexual_role.closing.other";
}

export default function CreateMemberSexualRole() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);

  const [memberChoice, setMemberChoice] = useState("");
  const [partnerChoice, setPartnerChoice] = useState("");

  const [memberCustom, setMemberCustom] = useState("");
  const [partnerCustom, setPartnerCustom] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.sexual_role.title", {
    defaultValue: "Sexual role",
  });
  const subtitle = !isCouple
    ? t("onboarding.sexual_role.single.question", {
        defaultValue: "What role feels most like your vibe?",
      })
    : t("onboarding.sexual_role.couple.lead", {
        defaultValue: "Choose one for you, and one for your partner.",
      });

  const coupleDetail = isCouple
    ? t("onboarding.sexual_role.couple.details", {
        defaultValue: "This helps us match energy and keep things respectful.",
      })
    : "";

  const partnerLabel = t("onboarding.sexual_role.partner.label", {
    defaultValue: "Now pick your partner’s sexual role",
  });

  const hintFallback =
    t("onboarding.sexual_role.tapToSelect", {
      defaultValue: "Tap to select your vibe 💫",
    }) || "Tap to select";

  const roles =
    Array.isArray(SEXUAL_ROLES) && SEXUAL_ROLES.length
      ? SEXUAL_ROLES
      : [
          "dominant",
          "submissive",
          "switch",
          "brat",
          "brat_tamer",
          "sensual_romantic",
          "experimental_kinky",
          "voyeur",
          "exhibitionist",
          "service_oriented",
          "role_player",
          "vanilla_gentle",
          "self_describe",
          "not_select",
        ];

  const options = roles
    .filter((v) => v !== "not_select")
    .map((value) => {
      const labelKey = `onboarding.sexual_role.options.${value}.label`;
      const hintKey = `onboarding.sexual_role.options.${value}.hint`;

      return {
        value,
        emoji: ROLE_META[value] || "🌙",
        title: t(labelKey, { defaultValue: value }),
        hint: t(hintKey, { defaultValue: "" }) || hintFallback,
      };
    });

  const memberNeedsCustom = memberChoice === "self_describe";
  const partnerNeedsCustom = partnerChoice === "self_describe";

  const cards = CARD_KEYS.map((key) => ({
    key,
    img: CARD_IMAGES[key],
    emoji: ROLE_META[key] || "🌙",
    title: t(`onboarding.sexual_role.cards.${key}.title`, {
      defaultValue: t(`onboarding.sexual_role.options.${key}.label`, {
        defaultValue: key,
      }),
    }),
    desc: t(`onboarding.sexual_role.cards.${key}.desc`, { defaultValue: "" }),
  }));

  const closingKey =
    memberChoice && (!isCouple || partnerChoice)
      ? getClosingKey({ isCouple, rawGender })
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoice) {
      toast.info(
        t("onboarding.sexual_role.toast.member_required", {
          defaultValue: "Please pick one for you.",
        }),
      );
      return;
    }

    if (memberNeedsCustom && !memberCustom.trim()) {
      toast.info(
        t("onboarding.sexual_role.toast.member_custom_required", {
          defaultValue: "Please type your role in your own words.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoice) {
      toast.info(
        t("onboarding.sexual_role.toast.partner_required", {
          defaultValue: "Please pick one for your partner.",
        }),
      );
      return;
    }

    if (isCouple && partnerNeedsCustom && !partnerCustom.trim()) {
      toast.info(
        t("onboarding.sexual_role.toast.partner_custom_required", {
          defaultValue: "Please type your partner’s role in their own words.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberSexualRole: memberChoice,
          partnerSexualRole: partnerChoice,
          ...(memberNeedsCustom
            ? { memberSexualRoleCustom: memberCustom.trim() }
            : {}),
          ...(partnerNeedsCustom
            ? { partnerSexualRoleCustom: partnerCustom.trim() }
            : {}),
        }
      : {
          memberSexualRole: memberChoice,
          ...(memberNeedsCustom
            ? { memberSexualRoleCustom: memberCustom.trim() }
            : {}),
        };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/sexual_role",
        payload,
        {
          skipToast: true,
        },
      );

      await refreshMe();
      navigate(data?.next || "/member/attraction_genders", { replace: true });
    } catch (err) {
      console.error("update sexual role failed:", err);
      toast.error(
        t("onboarding.sexual_role.toast.save_error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    !memberChoice ||
    (memberNeedsCustom && !memberCustom.trim()) ||
    (isCouple &&
      (!partnerChoice || (partnerNeedsCustom && !partnerCustom.trim())));

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{subtitle}</div>
              {isCouple && coupleDetail ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {coupleDetail}
                </div>
              ) : null}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {/* Member options */}
            <fieldset className={cls.options}>
              <legend className="sr-only">{subtitle}</legend>

              {options.map((opt) => {
                const checked = memberChoice === opt.value;

                return (
                  <label
                    key={`m-${opt.value}`}
                    className={`${cls.option} ${
                      checked ? cls.optionActive : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="memberSexualRole"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setMemberChoice(opt.value)}
                    />

                    <span className={cls.optionBody}>
                      <span className={cls.optionEmoji} aria-hidden="true">
                        {opt.emoji}
                      </span>

                      <span className={cls.optionText}>
                        <strong>{opt.title}</strong>
                        <small>{opt.hint}</small>
                      </span>

                      <span className={cls.optionDot} aria-hidden="true" />
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {memberNeedsCustom && (
              <div className={cls.sdWrap} role="status" aria-live="polite">
                <label className={cls.sdLabel}>
                  {t("onboarding.sexual_role.self_describe.label", {
                    defaultValue: "Tell us your role in your words",
                  })}
                  <input
                    type="text"
                    className={cls.sdInput}
                    value={memberCustom}
                    onChange={(e) => setMemberCustom(e.target.value)}
                    placeholder={t(
                      "onboarding.sexual_role.self_describe.placeholder",
                      {
                        defaultValue: "Type here…",
                      },
                    )}
                    autoComplete="off"
                  />
                </label>
                <div className={cls.sdHelp}>
                  {t("onboarding.sexual_role.self_describe.note", {
                    defaultValue: "We’ll save it exactly the way you write it.",
                  })}
                </div>
              </div>
            )}

            {/* Partner options (couple only) */}
            {isCouple && (
              <div className={cls.partnerBlock}>
                <div className={cls.partnerTitle}>{partnerLabel}</div>

                <fieldset className={cls.options}>
                  <legend className="sr-only">{partnerLabel}</legend>

                  {options.map((opt) => {
                    const checked = partnerChoice === opt.value;

                    return (
                      <label
                        key={`p-${opt.value}`}
                        className={`${cls.option} ${
                          checked ? cls.optionActive : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="partnerSexualRole"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setPartnerChoice(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>

                {partnerNeedsCustom && (
                  <div className={cls.sdWrap} role="status" aria-live="polite">
                    <label className={cls.sdLabel}>
                      {t("onboarding.sexual_role.self_describe.label", {
                        defaultValue:
                          "Tell us your partner’s role in their words",
                      })}
                      <input
                        type="text"
                        className={cls.sdInput}
                        value={partnerCustom}
                        onChange={(e) => setPartnerCustom(e.target.value)}
                        placeholder={t(
                          "onboarding.sexual_role.self_describe.placeholder",
                          {
                            defaultValue: "Type here…",
                          },
                        )}
                        autoComplete="off"
                      />
                    </label>
                    <div className={cls.sdHelp}>
                      {t("onboarding.sexual_role.self_describe.note", {
                        defaultValue:
                          "We’ll save it exactly the way you write it.",
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            {/* Submit */}
            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>

          {/* After-form cards */}
          <section
            className={cls.cardsSection}
            aria-label="Sexual role guide cards"
          >
            <div className={cls.cardsHeader}>
              <div className={cls.cardsTitle}>
                {t("onboarding.sexual_role.guide.title", {
                  defaultValue: "Quick guide (if you’re curious)",
                })}
              </div>
              <div className={cls.cardsSub}>
                {t("onboarding.sexual_role.guide.body", {
                  defaultValue:
                    "Pick what feels right above. These are simple explanations to make it easier.",
                })}
              </div>
            </div>

            <div className={cls.cardsGrid}>
              {cards.map((c) => (
                <article key={c.key} className={cls.gCard}>
                  <div className={cls.gCardMedia}>
                    <img
                      className={cls.gCardImg}
                      src={c.img}
                      alt=""
                      loading="lazy"
                      draggable="false"
                    />
                    <div className={cls.gCardTint} />
                    <div className={cls.gCardIcon} aria-hidden="true">
                      <span className={cls.cardEmoji}>{c.emoji}</span>
                    </div>
                  </div>

                  <div className={cls.gCardBody}>
                    <div className={cls.gCardTitle}>{c.title}</div>
                    <div className={cls.gCardDesc}>{c.desc}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
